import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Link2, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import Actions from './Actions';

interface IMemberMobileLink {
  displayName: string;
  url: string;
  sequence: number;
}

const MemberMobileLinksSection = () => {
  const { t } = useTranslation();

  const [links, setLinks] = useState<IMemberMobileLink[]>([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const data = await api.get<{ value: string }>(
        '/backoffice/content/memberMobileLinks',
      );
      const parsed = JSON.parse(data.value || '[]');
      const sorted = (Array.isArray(parsed) ? parsed : []).sort(
        (a: IMemberMobileLink, b: IMemberMobileLink) => a.sequence - b.sequence,
      );
      setLinks(sorted);
      setDirty(false);
    } catch {
      setLinks([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = () => {
    setLinks([...links, { displayName: '', url: '', sequence: links.length }]);
    setDirty(true);
  };

  const handleRemove = (index: number) => {
    const updated = links
      .filter((_, i) => i !== index)
      .map((link, i) => ({ ...link, sequence: i }));
    setLinks(updated);
    setDirty(true);
  };

  const handleChange = (
    index: number,
    field: 'displayName' | 'url',
    value: string,
  ) => {
    const updated = links.map((link, i) =>
      i === index ? { ...link, [field]: value } : link,
    );
    setLinks(updated);
    setDirty(true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...links];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setLinks(updated.map((link, i) => ({ ...link, sequence: i })));
    setDirty(true);
  };

  const handleMoveDown = (index: number) => {
    if (index === links.length - 1) return;
    const updated = [...links];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setLinks(updated.map((link, i) => ({ ...link, sequence: i })));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      await api.put('/backoffice/content/memberMobileLinks', {
        value: JSON.stringify(links),
      });
      setDirty(false);
      toast.success(t('common.save_success'));
    } catch (err: any) {
      toast.error(err.message || t('common.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-foreground" />
            <h3 className="text-base font-medium text-foreground">
              {t('config.member_mobile_links')}
            </h3>
          </div>
          <Button variant="outline" size="sm" onClick={handleAdd}>
            <Plus className="w-4 h-4 mr-1" />
            {t('config.member_mobile_links_add')}
          </Button>
        </div>

        {links.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('config.member_mobile_links_empty')}
          </p>
        ) : (
          <div className="space-y-3">
            {links.map((link, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-3 border rounded-md"
              >
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={index === 0}
                    onClick={() => handleMoveUp(index)}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={index === links.length - 1}
                    onClick={() => handleMoveDown(index)}
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex-1 space-y-2">
                  <Input
                    value={link.displayName}
                    onChange={(e) =>
                      handleChange(index, 'displayName', e.target.value)
                    }
                    placeholder={t(
                      'config.member_mobile_links_display_name_placeholder',
                    )}
                  />
                  <Input
                    type="url"
                    value={link.url}
                    onChange={(e) => handleChange(index, 'url', e.target.value)}
                    placeholder={t(
                      'config.member_mobile_links_url_placeholder',
                    )}
                  />
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => handleRemove(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <Actions
          onCancel={load}
          onSave={handleSave}
          saving={saving}
          disabled={!dirty}
        />
      </CardContent>
    </Card>
  );
};

export default MemberMobileLinksSection;
